import type {
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import { createApi } from '@reduxjs/toolkit/query/react';

import {
  type IntakeSummaryFrontend,
  normalizeBackendSummary,
  summaryBackendToFrontend,
} from '../../components/intakes/form/formatter';
import { serializeFilterParams } from '../../utils/api-params';
import { DetailedMatter } from '../types/client-intake-types';
import type { DocumentInstanceDto } from '../types/document-types';
import type { NoteEntry } from '../types/intake-types';
import {
  CreateIntakePayload,
  ExportStatusResponse,
  Intake,
  IntakeHandlerValue,
  IntakeListItem,
  IntakeListMetadata,
  IntakeStatusValue,
  ScheduleCallbackPayload,
  StartExportResponse,
  UpdateIntakePayload,
} from '../types/intake-types';
import {
  IntakeFilterParams,
  PaginatedResponse,
} from '../types/pagination-types';
import { baseQueryWithErrorHandling } from './baseQuery';

interface UpdateIntakeStatusPayload {
  id: string;
  status: IntakeStatusValue;
  rejection_reason?: string;
}

interface UpdateIntakeHandlerPayload {
  id: string;
  handler: IntakeHandlerValue;
}

// Quick Check types
export enum QuickCheckVerdict {
  Sign = 'Sign',
  DontSign = "Don't Sign",
  Escalate = 'Escalate',
  InsufficientData = 'Insufficient Data',
}

export enum ReasoningItemStatus {
  Pass = 'pass',
  Fail = 'fail',
  Warning = 'warning',
}

export interface QuickCheckReasoningItem {
  status: ReasoningItemStatus;
  text: string;
}

export interface QuickCheckCTASuggestions {
  mark_as_qualified: boolean;
  mark_as_unqualified: boolean;
  send_loe: boolean;
  escalate: boolean;
}

export interface QuickCheckResponse {
  verdict: QuickCheckVerdict;
  recommendation: string;
  reasoning: QuickCheckReasoningItem[];
  missing_info: string[];
  cta_suggestions: QuickCheckCTASuggestions;
  checked_at: string;
  form_data_hash: string;
}

export interface QuickCheckRequest {
  form_data_snapshot?: Record<string, unknown> | null;
}

const DEFAULT_INTAKES_LIMIT = 50;
const DEFAULT_INTAKES_OFFSET = 0;
const STILL_TREATING_VALIDATION_MARKERS = [
  'medical_wage_history',
  'still_treating',
  'bool_parsing',
];

const buildIntakesQueryArgs = (
  params: Partial<IntakeFilterParams>,
): FetchArgs => ({
  url: 'api/intakes/',
  params: serializeFilterParams(params),
});

const isStillTreatingValidationError = (
  error: unknown,
): error is FetchBaseQueryError & {
  data: {
    error?: string;
    details?: string;
  };
} => {
  if (!error || typeof error !== 'object' || !('status' in error)) {
    return false;
  }

  const fetchError = error as FetchBaseQueryError;
  if (fetchError.status !== 400) {
    return false;
  }

  if (!fetchError.data || typeof fetchError.data !== 'object') {
    return false;
  }

  const data = fetchError.data as {
    error?: string;
    details?: string;
  };

  if (data.error !== 'validation error' || typeof data.details !== 'string') {
    return false;
  }

  const { details } = data;
  return STILL_TREATING_VALIDATION_MARKERS.every((marker) =>
    details.includes(marker),
  );
};

const extractPaginatedCount = (data: unknown): number | null => {
  if (!data || typeof data !== 'object' || !('count' in data)) {
    return null;
  }

  const { count } = data as { count?: unknown };
  return typeof count === 'number' ? count : null;
};

const recoverCorruptedIntakesPage = async (
  params: Partial<IntakeFilterParams>,
  baseQuery: (args: string | FetchArgs) => Promise<{
    data?: unknown;
    error?: FetchBaseQueryError;
  }>,
): Promise<
  | { data: PaginatedResponse<IntakeListItem> }
  | { error: FetchBaseQueryError }
> => {
  const requestedLimit = params.limit ?? DEFAULT_INTAKES_LIMIT;
  const requestedOffset = params.offset ?? DEFAULT_INTAKES_OFFSET;
  const targetValidCount = requestedOffset + requestedLimit;
  const recoveredItems: IntakeListItem[] = [];

  let totalCount: number | null = null;
  const countProbe = await baseQuery(
    buildIntakesQueryArgs({
      ...params,
      limit: 0,
      offset: 0,
    }),
  );

  if (countProbe.data) {
    totalCount = extractPaginatedCount(countProbe.data);
  }

  let cursor = 0;
  const fallbackScanLimit =
    totalCount ?? targetValidCount + requestedLimit + 25;

  const scanNextItem = async (): Promise<FetchBaseQueryError | null> => {
    if (recoveredItems.length >= targetValidCount || cursor >= fallbackScanLimit) {
      return null;
    }

    const currentOffset = cursor;
    cursor += 1;

    const result = await baseQuery(
      buildIntakesQueryArgs({
        ...params,
        limit: 1,
        offset: currentOffset,
      }),
    );

    if (result.data) {
      const page = result.data as PaginatedResponse<IntakeListItem>;
      if (totalCount === null) {
        totalCount = extractPaginatedCount(result.data);
      }
      if (page.results[0]) {
        recoveredItems.push(page.results[0]);
      }
      return scanNextItem();
    }

    if (isStillTreatingValidationError(result.error)) {
      return scanNextItem();
    }

    return result.error as FetchBaseQueryError;
  };

  const recoveryError = await scanNextItem();
  if (recoveryError) {
    return { error: recoveryError };
  }

  const recoveredCount =
    totalCount === null
      ? recoveredItems.length
      : recoveredItems.length + Math.max(totalCount - cursor, 0);

  return {
    data: {
      count: recoveredCount,
      next: null,
      previous: null,
      results: recoveredItems.slice(
        requestedOffset,
        requestedOffset + requestedLimit,
      ),
    },
  };
};

export const intakeApiClient = createApi({
  reducerPath: 'intakeApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['Intakes', 'Intake', 'BodyPartSuggestions'] as const,
  endpoints: (builder) => ({
    getIntakes: builder.query<
      PaginatedResponse<IntakeListItem>,
      Partial<IntakeFilterParams>
    >({
      queryFn: async (params, _api, _extraOptions, baseQuery) => {
        const safeParams = params ?? {};
        const initialResult = await baseQuery(
          buildIntakesQueryArgs(safeParams),
        );

        if (initialResult.data) {
          return {
            data: initialResult.data as PaginatedResponse<IntakeListItem>,
          };
        }

        if (!isStillTreatingValidationError(initialResult.error)) {
          return { error: initialResult.error as FetchBaseQueryError };
        }

        return recoverCorruptedIntakesPage(safeParams, baseQuery);
        return recoverCorruptedIntakesPage(
          safeParams,
          baseQuery,
        );
      },
      providesTags: ['Intakes'],
      keepUnusedDataFor: 0,
    }),
    getIntakeListMetadata: builder.query<IntakeListMetadata, void>({
      query: () => 'api/intake-list-metadata/',
      providesTags: ['Intakes'],
    }),
    getIntake: builder.query<Intake, string>({
      query: (id) => `api/intakes/${id}/`,
      providesTags: (result, error, id) => [{ type: 'Intake', id }],
      // Don't serve stale cached data when switching between intakes
      keepUnusedDataFor: 0,
    }),
    createIntake: builder.mutation<Intake, CreateIntakePayload>({
      query: (intake) => ({
        url: `api/intakes/`,
        method: 'POST',
        body: intake,
      }),
      invalidatesTags: ['Intakes'],
    }),
    updateIntake: builder.mutation<Intake, UpdateIntakePayload>({
      query: ({ id, skipOptimistic, ...updates }) => {
        const body = (() => {
          // Summary semantics:
          // - undefined: omit from payload (no server change)
          // - null: explicitly clear persisted summary
          // - object: normalize to complete backend shape before sending
          if (updates.summary === undefined) return updates;
          return {
            ...updates,
            summary:
              updates.summary === null
                ? null
                : normalizeBackendSummary(updates.summary),
          };
        })();

        return {
          url: `api/intakes/${id}/`,
          method: 'PATCH',
          body,
        };
      },
      onQueryStarted: async (
        { id, skipOptimistic, ...updates },
        { dispatch, queryFulfilled },
      ) => {
        // Only perform optimistic updates for manual saves
        if (!skipOptimistic) {
          const patchResult = dispatch(
            intakeApiClient.util.updateQueryData('getIntake', id, (draft) => {
              // Update the draft with the new data
              if (updates.form_data) {
                draft.form_data = {
                  ...draft.form_data,
                  ...updates.form_data,
                };
              }
              if (updates.summary !== undefined) {
                // Keep optimistic cache behavior aligned with request semantics:
                // - null: clear summary in cache
                // - object: normalize before assigning
                draft.summary =
                  updates.summary === null
                    ? null
                    : normalizeBackendSummary(updates.summary);
              }
              if (updates.firm !== undefined) {
                draft.firm = updates.firm || '';
              }
              if (updates.assigned_to !== undefined) {
                draft.assigned_to = updates.assigned_to;
              }
              if (updates.manual_qualification !== undefined) {
                draft.manual_qualification = updates.manual_qualification;
              }
              if (updates.rejection_email_sent !== undefined) {
                draft.rejection_email_sent = updates.rejection_email_sent;
              }
              if (updates.is_scheduled !== undefined) {
                draft.is_scheduled = updates.is_scheduled;
              }
            }),
          );

          try {
            await queryFulfilled;
          } catch {
            // Rollback the optimistic update on failure
            patchResult.undo();
          }
        } else {
          // For auto-saves, just wait for the query to complete
          await queryFulfilled;
        }
      },
      // Only invalidate on success to prevent refetch loops on error.
      // Skip invalidation for auto-saves (skipOptimistic=true) to prevent
      // the refetch from overwriting local form state that changed while
      // the save was in-flight (e.g. a dropdown selection made after the
      // blur-triggered save started).
      invalidatesTags: (result, error, { id, skipOptimistic }) =>
        error || skipOptimistic ? [] : [{ type: 'Intake', id }, 'Intakes'],
    }),
    // Save companions without invalidating caches to avoid race conditions
    saveCompanions: builder.mutation<
      Intake,
      {
        id: string;
        companions: Array<{
          client_name: string;
          details: string | null;
          created_intake_id: string | null;
        }>;
      }
    >({
      query: ({ id, companions }) => ({
        url: `api/intakes/${id}/`,
        method: 'PATCH',
        body: { form_data: { companions } },
      }),
      // Do not invalidate; we only need to persist before create-from-row
      invalidatesTags: () => [],
    }),
    // New: Update persisted Intake status
    intakesSetStatusCreate: builder.mutation<Intake, UpdateIntakeStatusPayload>(
      {
        query: ({ id, status, rejection_reason }) => ({
          url: `api/intakes/${id}/status/`,
          method: 'POST',
          body: rejection_reason ? { status, rejection_reason } : { status },
        }),
        async onQueryStarted({ id, status }, { dispatch, queryFulfilled }) {
          // Optimistically update the intake detail cache
          const patchResult = dispatch(
            intakeApiClient.util.updateQueryData('getIntake', id, (draft) => {
              // Update top-level computed status using strong typing
              draft.status = status;
            }),
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
          }
        },
        // Only invalidate on success to prevent refetch loops on error
        invalidatesTags: (result, error, { id }) =>
          error ? [] : [{ type: 'Intake', id }, 'Intakes'],
      },
    ),
    // Update intake handler (Finch or Firm)
    intakesSetHandlerCreate: builder.mutation<
      Intake,
      UpdateIntakeHandlerPayload
    >({
      query: ({ id, handler }) => ({
        url: `api/intakes/${id}/handler/`,
        method: 'POST',
        body: { handler },
      }),
      async onQueryStarted({ id, handler }, { dispatch, queryFulfilled }) {
        // Optimistically update the intake detail cache
        const patchResult = dispatch(
          intakeApiClient.util.updateQueryData('getIntake', id, (draft) => {
            draft.handler = handler;
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      // Only invalidate on success to prevent refetch loops on error
      invalidatesTags: (result, error, { id }) =>
        error ? [] : [{ type: 'Intake', id }, 'Intakes'],
    }),
    convertIntakeToMatter: builder.mutation<DetailedMatter, string>({
      query: (intakeId) => ({
        url: `api/intakes/${intakeId}/convert_to_matter/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, intakeId) => [
        { type: 'Intake', id: intakeId },
        'Intakes',
      ],
    }),
    startIntakeExport: builder.mutation<StartExportResponse, string>({
      query: (intakeId) => ({
        url: `api/intakes/${intakeId}/export/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, intakeId) => [
        { type: 'Intake', id: intakeId },
      ],
    }),
    getIntakeExportStatus: builder.query<ExportStatusResponse, string>({
      query: (intakeId) => `api/intakes/${intakeId}/export/status/`,
    }),
    getIntakeMetadata: builder.query<Record<string, string | null>, string>({
      query: (intakeId) => `api/intakes/${intakeId}/metadata/`,
      providesTags: (result, error, id) => [{ type: 'Intake', id }],
    }),
    sendLoe: builder.mutation<
      { matter_id: string; document: DocumentInstanceDto },
      {
        intakeId: string;
        data: {
          document_template_id: string;
          merge_fields: Record<string, string>;
        };
      }
    >({
      query: ({ intakeId, data }) => ({
        url: `api/intakes/${intakeId}/send-loe/`,
        method: 'POST',
        body: data,
      }),
      // No cache invalidation — sending a document doesn't change form_data.
      // Invalidating here triggers a refetch cascade that wipes unsaved V2 data.
    }),
    sendNewLoe: builder.mutation<
      { matter_id: string; document: DocumentInstanceDto },
      {
        intakeId: string;
        data: {
          document_template_id: string;
          merge_fields: Record<string, string>;
          cancel_previous?: boolean;
        };
      }
    >({
      query: ({ intakeId, data }) => ({
        url: `api/intakes/${intakeId}/send-loe-new/`,
        method: 'POST',
        body: data,
      }),
      // No cache invalidation — same reason as sendLoe above.
    }),
    archiveIntake: builder.mutation<Intake, string>({
      query: (intakeId) => ({
        url: `api/intakes/${intakeId}/archive/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, intakeId) => [
        { type: 'Intake', id: intakeId },
        'Intakes',
      ],
    }),
    voidLatestLoe: builder.mutation<
      { matter_id: string; document: DocumentInstanceDto },
      string
    >({
      query: (intakeId) => ({
        url: `api/intakes/${intakeId}/void-loe/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, intakeId) => [
        { type: 'Intake', id: intakeId },
        'Intakes',
      ],
    }),
    unarchiveIntake: builder.mutation<Intake, string>({
      query: (intakeId) => ({
        url: `api/intakes/${intakeId}/unarchive/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, intakeId) => [
        { type: 'Intake', id: intakeId },
        'Intakes',
      ],
    }),
    // Narrow PATCH to update the structured intake notes log only
    updateIntakeNotesLog: builder.mutation<
      Intake,
      { id: string; general_notes_log: NoteEntry[] }
    >({
      query: ({ id, general_notes_log }) => ({
        url: `api/intakes/${id}/`,
        method: 'PATCH',
        body: { form_data: { general_notes_log } },
      }),
      // No onQueryStarted / optimistic cache update here.
      // addGeneralNote already handles the optimistic UI update via setForm.
      // An RTK cache update would change intakeData → trigger useIntakeForm's
      // useEffect → replace the entire form via convertBackendToFrontend,
      // which can revert unsaved local edits and trigger the auto-save cascade.
      //
      // No invalidatesTags either — a refetch would cause the same cascade.
      // The cache becomes stale for general_notes_log until the next navigation
      // (keepUnusedDataFor: 0 ensures a fresh GET on re-mount).
    }),
    // Generate Intake Summary (sync) and return persisted summary in camelCase
    generateIntakeSummary: builder.mutation<IntakeSummaryFrontend, string>({
      query: (intakeId) => ({
        url: `api/intakes/${intakeId}/summary/generate/`,
        method: 'POST',
      }),
      transformResponse: summaryBackendToFrontend,
      // Touch Intake detail cache so UIs depending on it can refetch if needed
      invalidatesTags: (result, error, intakeId) => [
        { type: 'Intake', id: intakeId },
      ],
    }),
    createCompanionFromRow: builder.mutation<
      Intake,
      { intakeId: string; rowIndex: number }
    >({
      query: ({ intakeId, rowIndex }) => ({
        url: `api/intakes/${intakeId}/companions/create-from-row/`,
        method: 'POST',
        body: { row_index: rowIndex },
      }),
      invalidatesTags: (result, error, { intakeId }) => [
        { type: 'Intake', id: intakeId },
        'Intakes',
      ],
    }),
    syncCompanionIntake: builder.mutation<
      Intake,
      { intakeId: string; targetIntakeId: string }
    >({
      query: ({ intakeId, targetIntakeId }) => ({
        url: `api/intakes/${intakeId}/companions/sync/`,
        method: 'POST',
        body: { target_intake_id: targetIntakeId },
      }),
      invalidatesTags: (result, error, { intakeId, targetIntakeId }) => [
        { type: 'Intake', id: intakeId },
        { type: 'Intake', id: targetIntakeId },
      ],
    }),
    runQuickCheck: builder.mutation<
      QuickCheckResponse,
      {
        intakeId: string;
        data?: QuickCheckRequest;
      }
    >({
      query: ({ intakeId, data }) => ({
        url: `api/intakes/${intakeId}/quick-check/`,
        method: 'POST',
        body: data || {},
      }),
      // Don't invalidate tags - this is read-only analysis
    }),
    // Escalate intake to Slack for review
    escalateIntake: builder.mutation<
      { status: string },
      {
        intakeId: string;
        data: {
          should_qualify: boolean;
          reason?: string;
          screening_link: string;
          client_name?: string;
          case_type?: string;
          state?: string;
          firm_name?: string;
          intaker_email?: string;
          intaker_name?: string;
        };
      }
    >({
      query: ({ intakeId, data }) => ({
        url: `api/intakes/${intakeId}/escalate/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { intakeId }) => [
        { type: 'Intake', id: intakeId },
        'Intakes',
      ],
    }),
    // Reassign intake to a different matter
    reassignIntake: builder.mutation<
      Intake,
      { intakeId: string; targetMatterId: string }
    >({
      query: ({ intakeId, targetMatterId }) => ({
        url: `api/intakes/${intakeId}/reassign/`,
        method: 'POST',
        body: { target_matter_id: targetMatterId },
      }),
      invalidatesTags: (result, error, { intakeId }) => [
        { type: 'Intake', id: intakeId },
        'Intakes',
      ],
    }),
    // End Intake - record first contact outcome
    endIntake: builder.mutation<
      { status: string; first_contact_outcome: string },
      {
        intakeId: string;
        data: {
          outcome: 'completed' | 'incomplete' | 'unavailable';
          missing_fields: Array<{
            screen: string;
            field_id: string;
            label: string;
          }>;
          screening_link: string;
          client_name?: string;
          firm_name?: string;
          retainer_status?: 'sent' | 'not_sent';
          rejection_reason?: string;
          intaker_email?: string;
          intaker_name?: string;
          unavailable_reason?: string;
          qualification_status?: string;
        };
      }
    >({
      query: ({ intakeId, data }) => ({
        url: `api/intakes/${intakeId}/end-intake/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { intakeId }) => [
        { type: 'Intake', id: intakeId },
        'Intakes',
      ],
    }),
    // Mark intake as no matter needed
    setNoMatterNeeded: builder.mutation<
      Intake,
      { intakeId: string; value?: boolean }
    >({
      query: ({ intakeId, value }) => ({
        url: `api/intakes/${intakeId}/no-matter-needed/`,
        method: 'POST',
        body: { value: value ?? true },
      }),
      invalidatesTags: (result, error, { intakeId }) => [
        { type: 'Intake', id: intakeId },
        'Intakes',
      ],
    }),
    // Schedule a callback for this intake (creates Google Calendar event)
    scheduleCallback: builder.mutation<Intake, ScheduleCallbackPayload>({
      query: ({ intakeId, ...body }) => ({
        url: `api/intakes/${intakeId}/schedule/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { intakeId }) => [
        { type: 'Intake', id: intakeId },
        'Intakes',
      ],
    }),
    // Cancel a scheduled callback
    cancelSchedule: builder.mutation<Intake, string>({
      query: (intakeId) => ({
        url: `api/intakes/${intakeId}/cancel-schedule/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, intakeId) => [
        { type: 'Intake', id: intakeId },
        'Intakes',
      ],
    }),
    // Mark client as called - records or clears timestamp when call was made
    markClientCalled: builder.mutation<
      { status: string; called_client_at: string | null },
      { intakeId: string; clear?: boolean }
    >({
      query: ({ intakeId, clear }) => ({
        url: `api/intakes/${intakeId}/mark-called/`,
        method: 'POST',
        body: { clear: clear ?? false },
      }),
      invalidatesTags: (result, error, { intakeId }) => [
        { type: 'Intake', id: intakeId },
        'Intakes',
      ],
    }),
    // Body part suggestions (shared across all intakes)
    getBodyPartSuggestions: builder.query<{ id: string; name: string }[], void>({
      query: () => 'api/body-part-suggestions/',
      providesTags: ['BodyPartSuggestions'],
    }),
    createBodyPartSuggestion: builder.mutation<
      { id: string; name: string },
      { name: string }
    >({
      query: (body) => ({
        url: 'api/body-part-suggestions/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['BodyPartSuggestions'],
    }),
  }),
});

export const {
  useGetIntakesQuery,
  useGetIntakeListMetadataQuery,
  useGetIntakeQuery,
  useCreateIntakeMutation,
  useUpdateIntakeMutation,
  useConvertIntakeToMatterMutation,
  useStartIntakeExportMutation,
  useGetIntakeExportStatusQuery,
  useGetIntakeMetadataQuery,
  useSendLoeMutation,
  useSendNewLoeMutation,
  useArchiveIntakeMutation,
  useUnarchiveIntakeMutation,
  useVoidLatestLoeMutation,
  useUpdateIntakeNotesLogMutation,
  useIntakesSetStatusCreateMutation,
  useIntakesSetHandlerCreateMutation,
  useGenerateIntakeSummaryMutation,
  // Companion cases hooks
  useCreateCompanionFromRowMutation,
  useSyncCompanionIntakeMutation,
  useSaveCompanionsMutation,
  // Quick Check hook
  useRunQuickCheckMutation,
  // Escalation hook
  useEscalateIntakeMutation,
  // Reassignment hook
  useReassignIntakeMutation,
  // End Intake hook
  useEndIntakeMutation,
  // Mark client called hook
  useMarkClientCalledMutation,
  // No matter needed hook
  useSetNoMatterNeededMutation,
  // Schedule callback hooks
  useScheduleCallbackMutation,
  useCancelScheduleMutation,
  // Body part suggestions hooks
  useGetBodyPartSuggestionsQuery,
  useCreateBodyPartSuggestionMutation,
} = intakeApiClient;
